import api from '../../../utils/api';

/**
 * Service for managing recurring transactions
 */
const recurringTransactionService = {
  /**
   * Get recurring transactions for the current user
   * @param {Object} options - Options for recurring transaction detection
   * @param {boolean} options.includeDeposits - Whether to include deposits (positive amounts)
   * @param {number} options.minOccurrences - Minimum number of occurrences for a transaction to be considered recurring
   * @returns {Promise} - Promise resolving to recurring transactions data
   */
  getRecurringTransactions: async (options = {}) => {
    try {
      // Build query parameters
      const queryParams = new URLSearchParams();
      
      if (options.includeDeposits !== undefined) {
        queryParams.append('includeDeposits', options.includeDeposits);
      }
      
      if (options.minOccurrences !== undefined) {
        queryParams.append('minOccurrences', options.minOccurrences);
      }
      
      const queryString = queryParams.toString();
      const url = `/api/transactions/recurring${queryString ? `?${queryString}` : ''}`;
      
      const response = await api.get(url);
      return response.data;
    } catch (error) {
      console.error('Error getting recurring transactions:', error);
      throw error;
    }
  },
  
  /**
   * Format a recurring pattern type for display
   * @param {Object} pattern - The detected pattern
   * @returns {String} - Formatted pattern text
   */
  formatPatternType: (pattern) => {
    if (!pattern || !pattern.type) {
      return 'Unknown';
    }
    
    const { type, confidence } = pattern;
    
    let patternText = 'Unknown';
    
    switch (type) {
      case 'monthly':
        patternText = 'Monthly';
        break;
      case 'weekly':
        patternText = 'Weekly';
        break;
      case 'biweekly':
        patternText = 'Bi-weekly';
        break;
      case 'yearly':
        patternText = 'Yearly';
        break;
      case 'custom':
        if (pattern.averageInterval) {
          patternText = `Every ${Math.round(pattern.averageInterval)} days`;
        } else {
          patternText = 'Custom';
        }
        break;
      case 'irregular':
        patternText = 'Irregular';
        break;
      default:
        patternText = 'Unknown';
    }
    
    // Add confidence if available
    if (confidence !== undefined) {
      patternText += ` (${confidence}% confidence)`;
    }
    
    return patternText;
  },
  
  /**
   * Get class name for confidence level
   * @param {Number} confidence - Confidence score (0-100)
   * @returns {String} - CSS class name
   */
  getConfidenceClass: (confidence) => {
    if (confidence === undefined) return 'text-gray-500';
    
    if (confidence >= 80) return 'text-green-600';
    if (confidence >= 60) return 'text-blue-600';
    if (confidence >= 40) return 'text-yellow-600';
    return 'text-red-600';
  }
};

export default recurringTransactionService; 